/**
 * Character definitions – the roster is data (see `@archon3d/content`). Numbers here drive both
 * the board (movement, tier) and the arena (hit points, attack); the arena details grow in M2.
 */
import type { SpellId } from '../game/types';

export type MovementType = 'ground' | 'fly' | 'teleport';
/**
 * How a character fights. `burn` is the odd one out: it has no weapon and throws nothing. Holding
 * the attack raises a ring of cold fire around the character that burns whatever stands inside it,
 * for as long as the account behind it lasts (`battle/burn.ts`).
 */
export const ATTACK_TYPES = ['melee', 'projectile', 'aura', 'burst', 'mimic', 'burn'] as const;
export type AttackType = (typeof ATTACK_TYPES)[number];
export const DAMAGE_TYPES = [
  'physical',
  'piercing',
  'fire',
  'frost',
  'arcane',
  'venom',
  'lightning',
] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];

export interface CharacterDef {
  id: string;
  name: string;
  /** Relative value 1 (pawn) … 5 (leader); drives roster substitution and the AI's evaluation. */
  tier: number;
  /**
   * Strength rating 10 … 100 computed by `tools/balance` (`characterForce` in the core) from the
   * duel odds against the rest of the roster, hit points, attack, movement and spells. Stored so
   * team sums can be compared without recomputing; absent until the tool has been run.
   */
  force?: number;
  /** Which of the original 16 this character stands in for, when the roster is played in classic mode. */
  classicEquivalent?: string;
  movement: { type: MovementType; range: number };
  /** Base hit points before the luminosity bonus. */
  hitPoints: number;
  /**
   * The weapon. `damage` is nominal, before the blow's die roll. `interval` is the recovery in
   * seconds before the character can strike again — a heavy bolt takes longer to gather than an
   * arrow, which is what keeps a dragon from out-shooting an archer simply by hitting harder. A
   * character that leaves it out recovers at its type's pace (melee 0.6 s, aura 0.5 s, anything
   * thrown 1 s). The shapeshifter copies its opponent's weapon, recovery and all.
   */
  attack: {
    type: AttackType;
    damage: number;
    interval?: number;
    /** Theme of the blow; omitted content remains ordinary physical damage. */
    damageType?: DamageType;
    /** Chance that a geometrically successful blow takes effect, before protection. */
    effectChance?: number;
    /** For a burning ring, reach as a multiple of the fighter's height (default 2). */
    reach?: number;
    /** For a burning ring, seconds held by a full account (default 3). */
    duration?: number;
  };
  /** Arena-only physical traits; omitted values preserve the legacy behaviour. */
  combat?: {
    /** Multiplier on the size-derived movement speed. */
    speed?: number;
    /** Chance 0…0.75 to turn an otherwise effective blow aside. */
    protection?: number;
    /** Fraction 0…0.95 of each named damage type absorbed. */
    resistances?: Partial<Record<DamageType, number>>;
    /** Optional non-Expert corrections applied after the shared duel-level profile. */
    levels?: Partial<Record<'novice' | 'fair' | 'master', CombatLevelAdjustment>>;
    /** Per-game AI decision factors relative to the engine's Expert decision profile. */
    aiLevels?: Partial<
      Record<'novice' | 'fair' | 'master', Partial<Record<CombatAiProperty, number>>>
    >;
  };
  /** Leaders can cast the board spells. */
  spellcaster?: boolean;
  /**
   * Magic this character carries, and how much of it. `spellPoints` is the most it can hold and
   * what it starts a game with; `spells` is what it knows. A character with neither is no magician
   * — most of the roster — and its tablet shows every spell greyed.
   *
   * Two mages know everything and hold the most; a few others know a little in their own line, so
   * that losing your mage is not the end of magic and so that which piece you select matters.
   */
  spellPoints?: number;
  spells?: SpellId[];
  /**
   * Never starts on a board: a spell brings it (Summon Elemental) and it is gone again after its
   * fight. Boards may not place it, the editor does not offer it, the balance tool leaves it out
   * of team sums, and it takes no luminosity bonus and never enters a graveyard.
   */
  summoned?: boolean;
  /** 3D model; pieces without one render as simple placeholder shapes. */
  model?: CharacterModel;
  /**
   * How the character sounds. Every voice is synthesised from a hash of the id, so this only
   * tunes it; a field left out keeps its derived value.
   */
  voice?: CharacterVoice;
}

/** Multipliers, never absolute stats: Expert base × shared level × these factors. */
export interface CombatLevelAdjustment {
  hitPoints?: number;
  damage?: number;
  attackInterval?: number;
  effectChance?: number;
  protection?: number;
}

export const COMBAT_AI_PROPERTIES = [
  'reaction',
  'anticipation',
  'moveScale',
  'turnRate',
  'cadence',
  'aimSpread',
  'lead',
  'notice',
  'carefulBelow',
  'coverRadius',
  'weave',
  'fireTolerance',
  'rangeWobble',
  'rangedDistance',
] as const;
export type CombatAiProperty = (typeof COMBAT_AI_PROPERTIES)[number];

export type Vowel = 'a' | 'e' | 'i' | 'o' | 'u';
export const VOWELS: readonly Vowel[] = ['a', 'e', 'i', 'o', 'u'];

export interface CharacterVoice {
  /** Base pitch in Hz: about 70 for a giant, 400 for a sprite. */
  pitch?: number;
  /** Size of the throat: 1 is human, below 1 bigger (deeper vowels), above 1 smaller. */
  formantScale?: number;
  /** 0 (clean) … 1 (rough, growling). */
  growl?: number;
  /** 0 (clear) … 1 (hoarse, whispery). */
  breathiness?: number;
  /** Vowels the character favours; one or more of a e i o u. */
  vowels?: Vowel[];
  /** How fast it speaks: 1 is normal, 0.5 half as fast, 2 twice as fast. */
  tempo?: number;
}

export interface CharacterModel {
  /** GLB path relative to the client's public root, e.g. `/characters/paladin.glb`. */
  file: string;
  /**
   * The same character made light for the board (`tools/assets/boardModel.mjs`): a piece stands a
   * few centimetres tall, where the duel's full model would spend most of the headset's budget.
   * The duel always shows `file`; the board shows `file` when there is no board model.
   */
  boardFile?: string;
  /** Height of the piece on the board in metres (a human ≈ 0.09 at table scale). */
  boardHeight?: number;
  /** Extra yaw in degrees if the model does not face +Z. */
  yaw?: number;
  /**
   * How the piece crosses the board: walking square by square, flying in an arc, drifting just
   * above the squares like a ghost, or blinking out and in. Defaults from the movement type
   * (ground → walk, fly → fly, teleport → blink).
   */
  motion?: MotionStyle;
  /** Animation clip names for game actions. */
  clips?: Partial<Record<ClipAction, string>>;
}

/**
 * How a piece crosses the board: on foot, in hops from square to square, on the wing, drifting
 * above the tiles, or blinking out and in.
 */
export type MotionStyle = 'walk' | 'jump' | 'fly' | 'float' | 'blink';

/** The game actions a model's clips are named for; any other key would never be played. */
export const CLIP_ACTIONS = [
  'idle',
  'walk',
  'run',
  'jump',
  'fly',
  'attack',
  'hit',
  'death',
  'victory',
] as const;
export type ClipAction = (typeof CLIP_ACTIONS)[number];

/** The motion a character uses on the board unless its model says otherwise. */
export function motionStyle(character: Pick<CharacterDef, 'movement' | 'model'>): MotionStyle {
  if (character.model?.motion) return character.model.motion;
  switch (character.movement.type) {
    case 'fly':
      return 'fly';
    case 'teleport':
      return 'blink';
    default:
      return 'walk';
  }
}

export type Roster = Readonly<Record<string, CharacterDef>>;
